import React, { useState } from 'react'
import styles from './CreatePostModal.module.scss'

const CreatePostModal = ({ isOpen, onClose, onSubmit, categories = [] }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    image: null,
    video_url: '',
    external_link: '',
    tags: ''
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  if (!isOpen) return null

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Очищаем ошибку при изменении поля
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Заголовок обязателен'
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Содержание обязательно'
    }

    if (!formData.category) {
      newErrors.category = 'Выберите категорию'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      // Подготавливаем данные для отправки
      const submitData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        category: formData.category,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : []
      }

      // Добавляем опциональные поля
      if (formData.image) {
        submitData.image = formData.image
      }
      if (formData.video_url.trim()) {
        submitData.video_url = formData.video_url.trim()
      }
      if (formData.external_link.trim()) {
        submitData.external_link = formData.external_link.trim()
      }

      await onSubmit(submitData)

      // Сбрасываем форму
      setFormData({
        title: '',
        content: '',
        category: '',
        image: null,
        video_url: '',
        external_link: '',
        tags: ''
      })
      setErrors({})
      onClose()
    } catch (error) {
      console.error('Error creating post:', error)
      setErrors({ submit: 'Ошибка при создании поста. Попробуйте еще раз.' })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setFormData({
        title: '',
        content: '',
        category: '',
        image: null,
        video_url: '',
        external_link: '',
        tags: ''
      })
      setErrors({})
      onClose()
    }
  }

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Создать новый пост</h3>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalBody}>
          <div className={styles.formGroup}>
            <label htmlFor="title">Заголовок *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Введите заголовок поста"
              className={errors.title ? styles.error : ''}
              disabled={loading}
            />
            {errors.title && <span className={styles.errorText}>{errors.title}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="category">Категория *</label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className={errors.category ? styles.error : ''}
              disabled={loading}
            >
              <option value="">Выберите категорию</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            {errors.category && <span className={styles.errorText}>{errors.category}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="content">Содержание *</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              placeholder="Напишите ваш пост..."
              rows={6}
              className={errors.content ? styles.error : ''}
              disabled={loading}
            />
            {errors.content && <span className={styles.errorText}>{errors.content}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="image">Изображение</label>
            <input
              type="file"
              id="image"
              accept="image/*"
              onChange={handleImageChange}
              disabled={loading}
            />
            <small className={styles.hint}>Максимальный размер: 5MB. Форматы: JPG, PNG, GIF</small>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="video_url">Ссылка на видео</label>
            <input
              type="url"
              id="video_url"
              name="video_url"
              value={formData.video_url}
              onChange={handleInputChange}
              placeholder="https://youtube.com/watch?v=..."
              disabled={loading}
            />
            <small className={styles.hint}>Поддерживаются YouTube, Vimeo и другие платформы</small>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="external_link">Внешняя ссылка</label>
            <input
              type="url"
              id="external_link"
              name="external_link"
              value={formData.external_link}
              onChange={handleInputChange}
              placeholder="https://example.com"
              disabled={loading}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="tags">Теги</label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              placeholder="тег1, тег2, тег3"
              disabled={loading}
            />
            <small className={styles.hint}>Разделяйте теги запятыми</small>
          </div>

          {errors.submit && (
            <div className={styles.errorMessage}>
              {errors.submit}
            </div>
          )}

          <div className={styles.modalFooter}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleClose}
              disabled={loading}
            >
              Отмена
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={loading}
            >
              {loading ? 'Создание...' : 'Создать пост'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreatePostModal